/**
 * Display-time formatting helpers for policy (and similar) titles.
 *
 * Auto-generated titles from QA / live-qa runs frequently embed raw ISO-8601
 * timestamps (e.g. `live-qa 2026-05-10T09:37:07Z`). These look like debug text
 * in the UI. {@link formatPolicyTitle} rewrites any embedded ISO timestamp
 * substring to a human-friendly form. {@link truncatePolicyTitle} clamps the
 * visible length and appends an ellipsis so list / card layouts have
 * predictable widths regardless of underlying title length.
 *
 * Both helpers are pure and reusable; planned reuse includes Acknowledgments
 * rows (#1819, #1862) where the same titles appear.
 */

/** Default visible-character budget for list and card titles. */
export const DEFAULT_TITLE_MAX_LENGTH = 80;

/** Single-character ellipsis (U+2026). Counted as one char in length budgets. */
const ELLIPSIS = '…';

/**
 * Matches a single ISO-8601 datetime substring with optional fractional seconds
 * and optional timezone designator (`Z` or `±HH:MM` / `±HHMM`).
 *
 * Anchored without word boundaries to preserve flexibility — the timestamp may
 * sit adjacent to non-word characters in the surrounding title.
 */
const ISO_TIMESTAMP_REGEX = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/g;

/**
 * Formats a `Date` to a stable human-friendly string in UTC.
 *
 * The UTC anchor matters: ISO timestamps embedded in policy titles are
 * generated by background jobs (live-qa runs, etc.) and the user reads them as
 * "when did the run happen" — not "what was the local clock at the user's
 * desk." Using UTC also makes test output deterministic across CI runners.
 *
 * Output shape: `May 10, 2026 9:37 AM` (month-day-year, 12-hour clock). We
 * assemble the parts manually rather than relying on the locale string because
 * `Intl.DateTimeFormat('en-US', ...).format(...)` inserts a stray comma
 * between the year and the time (`May 10, 2026, 9:37 AM`) which reads as
 * cluttered for title use.
 */
function formatDateUtc(date: Date): string {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).formatToParts(date);

    const get = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? '';

    return `${get('month')} ${get('day')}, ${get('year')} ${get('hour')}:${get('minute')} ${get('dayPeriod')}`;
}

/**
 * Replaces every ISO-8601 datetime substring inside `title` with a
 * human-formatted UTC date string.
 *
 * Non-matching input (including the empty string) is returned unchanged. If a
 * substring matches the ISO shape but does not parse to a valid `Date`, the
 * original substring is preserved — we never throw on malformed input.
 *
 * @param title Raw title text, possibly containing one or more ISO timestamps.
 * @returns The title with timestamps reformatted; original string otherwise.
 */
export function formatPolicyTitle(title: string): string {
    if (!title) {
        return title;
    }
    return title.replace(ISO_TIMESTAMP_REGEX, (match) => {
        // If the source omitted a timezone designator, Date.parse treats the
        // string as local time on some runtimes and UTC on others. Append `Z`
        // so the interpretation is stable.
        const hasTzDesignator = /Z$|[+-]\d{2}:?\d{2}$/.test(match);
        const normalized = hasTzDesignator ? match : `${match}Z`;
        const parsed = new Date(normalized);
        if (Number.isNaN(parsed.getTime())) {
            return match;
        }
        return formatDateUtc(parsed);
    });
}

/**
 * Truncates a title to `maxLength` characters, appending a single-character
 * ellipsis when truncation occurs.
 *
 * The returned string never exceeds `maxLength`. Titles already shorter than
 * or equal to `maxLength` are returned unchanged so call sites can use this
 * unconditionally on every render.
 *
 * @param title     The title to clamp. Empty input returns empty output.
 * @param maxLength Hard cap on the rendered length, including the ellipsis.
 *                  Defaults to {@link DEFAULT_TITLE_MAX_LENGTH}.
 */
export function truncatePolicyTitle(title: string, maxLength: number = DEFAULT_TITLE_MAX_LENGTH): string {
    if (!title || title.length <= maxLength) {
        return title;
    }
    return title.slice(0, maxLength - 1) + ELLIPSIS;
}
